import { observer } from 'mobx-react'
import React, { useContext, useEffect, useState } from 'react'
import clsx from 'clsx'
import './index.scss'
import DashboardMatchHistoryCard from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardMatchHistoryCard'
import DashboardLatestStatementsCard from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardLatestStatementsCard'
import DashboardRoyaltiesGraph from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardRoyaltiesGraph'
import {
    getEarningsForDistributionPeriods,
    getEarningsLastDistribution,
    getEarningsYearToDate,
} from '@App/api/routes/rightsholder/processing'
import { StoresContext } from '@App/contexts'
import DashboardWorksKpiCard from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardWorksKpiCard'
import DashboardContentHeader from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardContentHeader'
import DashboardKpiCard from '@Scenes/RightsholderScene/RightsholderDashboard/DashboardKpiCard'
import MemberHubDiagnostic from '@Scenes/RightsholderScene/RightsholderDashboard/MemberHubDiagnostic'
import { YearToDatePayment } from '@Entities/processing/year-to-date-payment'
import { LastDistributionPayment } from '@Entities/processing/last-distribution-payment'
import { DistributionPaid } from '@Entities/processing/distribution-paid'
import { analyticsSendPage } from '@App/common/utils/analytics-utils'
import { PERMISSIONS } from '@Const/permissions'
import useNextDistributionDate from '@App/common/hooks/use-next-distribution-date'

export const formatCurrency = (amount?: number) =>
    amount?.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }) || ''

const RightsholderDashboard = () => {
    const { rightsholderStore, catalogSearchStore, popupStore, rightsholderUserStore } = useContext(StoresContext)
    const [earningsStatistics, setEarningsStatistics] = useState<DistributionPaid[]>()
    const [earningsYTD, setEarningsYTD] = useState<YearToDatePayment>()
    const [earningsLastDist, setEarningsLastDist] = useState<LastDistributionPayment>()

    const hasRoyaltyAccess = rightsholderUserStore.doesRHUserHaveOneOfThesePermissionOrIsInternal([
        PERMISSIONS.REPORTING_PERMISSIONS.view_statements,
        PERMISSIONS.SUPER_USER_PERMISSIONS.manage_user_permissions,
    ])
    const nextDistributionDate = useNextDistributionDate(hasRoyaltyAccess)

    const destroySearchCatalogWorks = () => {
        catalogSearchStore.destroyWorksRightsHolderPaginationInfo()
        catalogSearchStore.destroyCurrentPage()
    }

    useEffect(() => {
        analyticsSendPage('rightsholder_home')
        destroySearchCatalogWorks()
    }, [])

    useEffect(() => {
        if (!hasRoyaltyAccess) {
            return
        }
        Promise.all([
            getEarningsForDistributionPeriods(rightsholderStore.currentRightsholderId, 6),
            getEarningsYearToDate(rightsholderStore.currentRightsholderId),
            getEarningsLastDistribution(rightsholderStore.currentRightsholderId),
        ])
            .then((result) => {
                setEarningsStatistics(result[0].data)
                setEarningsYTD(result[1].data)
                setEarningsLastDist(result[2].data)
            })
            .catch(() => popupStore.setAlertData('Unable To Load Dashboard Data', true))
    }, [hasRoyaltyAccess])

    const memberSummary = rightsholderStore.memberSummary

    return (
        <>
            <title>Summary</title>
            <div className="rightsholder-dashboard">
                <DashboardContentHeader />
                <MemberHubDiagnostic />
                <div className={clsx('dashboard-grid', !hasRoyaltyAccess && 'dashboard-grid-no-royalties')}>
                    {hasRoyaltyAccess && nextDistributionDate && (
                        <DashboardKpiCard
                            title="Your Next Distribution Date:"
                            data={nextDistributionDate}
                            className="next-distribution"
                        />
                    )}
                    {hasRoyaltyAccess && (
                        <DashboardKpiCard
                            title="Your amount paid last distribution"
                            data={formatCurrency(earningsLastDist?.amount)}
                            className="royalties-period"
                        />
                    )}
                    {hasRoyaltyAccess && (
                        <DashboardKpiCard
                            title="Amount paid to date this year"
                            data={formatCurrency(earningsYTD?.amount)}
                            className="royalties-year"
                        />
                    )}
                    <DashboardWorksKpiCard data={memberSummary.totalWorks} />
                    <DashboardKpiCard
                        title="Your pending works"
                        data={memberSummary.registrationInProgress.toLocaleString('en-US')}
                        className="pending-works"
                        tooltipContent="These are works registrations you have submitted that are waiting for review from our team."
                    />
                    <DashboardMatchHistoryCard />
                    {hasRoyaltyAccess && <DashboardRoyaltiesGraph data={earningsStatistics ?? []} />}
                    {hasRoyaltyAccess && <DashboardLatestStatementsCard />}
                </div>
            </div>
        </>
    )
}
export default observer(RightsholderDashboard)
